// Curated initial source seed lists per module.
// One-time bootstrap data, used by the generic ModuleSeed admin page.
// Signal sources map to POST /v1/sources. Data sources are validation /
// baseline references and MUST NOT be registered into the event ingestion
// pipeline.

import type { Module, SourceTier, SourceType } from "@bios/types";

export interface SeedSourceSpec {
  name: string;
  url: string;
  module: Module;
  type: SourceType;
  tier: SourceTier;
  trust_weight: number;
}

export interface DataSourceSpec {
  name: string;
  url: string;
  module: Module;
  type: "api" | "dataset" | "html_dashboard";
  tier: SourceTier;
  trust_weight: number;
  metrics: readonly ("employment_rate" | "wage_trend" | "placement_rate")[];
  use: "validation" | "baseline_metrics" | "cross_check";
}

const CAPITAL_SEED: readonly SeedSourceSpec[] = [
  {
    name: "SEC Form D Filings",
    url: "https://www.sec.gov/cgi-bin/browse-edgar?action=getcurrent&type=D&start=0&count=40&output=atom",
    module: "capital",
    type: "filing",
    tier: "primary",
    trust_weight: 0.95,
  },
  {
    name: "Crunchbase News",
    url: "https://news.crunchbase.com/feed/",
    module: "capital",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "TechCrunch Funding",
    url: "https://techcrunch.com/tag/funding/feed/",
    module: "capital",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.7,
  },
  {
    name: "Black Enterprise Money",
    url: "https://www.blackenterprise.com/category/money/feed/",
    module: "capital",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "Harlem Capital Blog",
    url: "https://harlem.capital/feed/",
    module: "capital",
    type: "press_release",
    tier: "secondary",
    trust_weight: 0.7,
  },
  {
    name: "Kapor Center",
    url: "https://www.kaporcenter.org/feed/",
    module: "capital",
    type: "press_release",
    tier: "secondary",
    trust_weight: 0.7,
  },
];

const EDUCATION_SEED: readonly SeedSourceSpec[] = [
  {
    name: "UNCF",
    url: "https://www.uncf.org/feed/",
    module: "education",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "Thurgood Marshall College Fund",
    url: "https://www.tmcf.org/feed/",
    module: "education",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "The Hechinger Report",
    url: "https://hechingerreport.org/feed/",
    module: "education",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "The 74",
    url: "https://www.the74million.org/feed/",
    module: "education",
    type: "rss",
    tier: "primary",
    trust_weight: 0.8,
  },
  {
    name: "EdSurge",
    url: "https://www.edsurge.com/feed",
    module: "education",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.7,
  },
  {
    name: "Black Chronicle",
    url: "https://blackchronicle.com/feed/",
    module: "education",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.65,
  },
];

// Phase 16.1: hand-curated policy sources. Rule: every source must help
// answer "did money actually move or did outcomes change?" Anything that
// only emits press-release narrative without a funding / enforcement /
// outcome signal is excluded by design.
const POLICY_SEED: readonly SeedSourceSpec[] = [
  {
    name: "Federal Register",
    url: "https://www.federalregister.gov/documents/search.rss?conditions%5Btype%5D%5B%5D=RULE&conditions%5Btype%5D%5B%5D=NOTICE",
    module: "policy",
    type: "rss",
    tier: "primary",
    trust_weight: 0.95,
  },
  {
    name: "Grants.gov",
    url: "https://www.grants.gov/rss/GrantsRSS.xml",
    module: "policy",
    type: "rss",
    tier: "primary",
    trust_weight: 0.9,
  },
  {
    name: "USAspending",
    url: "https://api.usaspending.gov/api/v2/awards/last_updated/",
    module: "policy",
    type: "api",
    tier: "primary",
    trust_weight: 0.95,
  },
  {
    name: "GovInfo Bills",
    url: "https://www.govinfo.gov/rss/bills.xml",
    module: "policy",
    type: "rss",
    tier: "primary",
    trust_weight: 0.9,
  },
  {
    name: "Congress.gov House Floor",
    url: "https://www.congress.gov/rss/house-floor-today.xml",
    module: "policy",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "GAO Reports",
    url: "https://www.gao.gov/rss.xml",
    module: "policy",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.8,
  },
  {
    name: "DOJ Civil Rights Division",
    url: "https://www.justice.gov/crt/press-releases/rss.xml",
    module: "policy",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.8,
  },
  {
    name: "Department of Education",
    url: "https://www.ed.gov/news/press-releases/rss.xml",
    module: "policy",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.75,
  },
  {
    name: "HUD",
    url: "https://www.hud.gov/rss/hud.xml",
    module: "policy",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.75,
  },
  {
    name: "SBA",
    url: "https://www.sba.gov/rss/press-releases.xml",
    module: "policy",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.75,
  },
];

const LEGAL_SEED: readonly SeedSourceSpec[] = [
  {
    name: "DOJ Civil Rights Division",
    url: "https://www.justice.gov/news/rss?field_component=436&groupname=56&require_all=0&search_api_language=en&show_public_archived=0&type%5B0%5D=blog_post&type%5B1%5D=press_release&type%5B2%5D=speech",
    module: "legal",
    type: "rss",
    tier: "primary",
    trust_weight: 0.9,
  },
  {
    name: "ACLU Press Releases",
    url: "https://www.aclu.org/press-releases/feed",
    module: "legal",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.75,
  },
  {
    name: "Lawyers' Committee for Civil Rights Under Law",
    url: "https://www.lawyerscommittee.org/feed/",
    module: "legal",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.78,
  },
  {
    name: "Civil Rights Corps Press Releases",
    url: "https://civilrightscorps.org/category/press-releases/feed/",
    module: "legal",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.76,
  },
];

export const WORKFORCE_SIGNAL_SOURCES: readonly SeedSourceSpec[] = [
  {
    name: "DOL Wage and Hour News Releases",
    url: "https://www.dol.gov/rss/releases.xml#whd",
    module: "workforce",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "DOL News Releases",
    url: "https://www.dol.gov/rss/releases.xml",
    module: "workforce",
    type: "rss",
    tier: "primary",
    trust_weight: 0.8,
  },
  {
    name: "DOL Grants",
    url: "https://www.dol.gov/rss/releases.xml#grants",
    module: "workforce",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "BLS Employment Cost Index Releases",
    url: "https://www.bls.gov/feed/eci.rss",
    module: "workforce",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
  {
    name: "BLS Employer Costs Releases",
    url: "https://www.bls.gov/feed/ecec.rss",
    module: "workforce",
    type: "rss",
    tier: "primary",
    trust_weight: 0.85,
  },
];

export const WORKFORCE_DATA_SOURCES: readonly DataSourceSpec[] = [
  {
    name: "BLS Public Data API",
    url: "https://api.bls.gov/publicAPI/v2/timeseries/data/",
    module: "workforce",
    type: "api",
    tier: "primary",
    trust_weight: 0.95,
    metrics: ["employment_rate", "wage_trend"],
    use: "baseline_metrics",
  },
  {
    name: "BLS QCEW Open Data",
    url: "https://www.bls.gov/cew/additional-resources/open-data/home.htm",
    module: "workforce",
    type: "dataset",
    tier: "primary",
    trust_weight: 0.95,
    metrics: ["employment_rate", "wage_trend"],
    use: "baseline_metrics",
  },
  {
    name: "DOL Open Data Portal",
    url: "https://data.dol.gov/",
    module: "workforce",
    type: "api",
    tier: "primary",
    trust_weight: 0.95,
    metrics: ["employment_rate", "wage_trend", "placement_rate"],
    use: "cross_check",
  },
  {
    name: "DOL ETA Performance Data",
    url: "https://www.dol.gov/agencies/eta/performance/results",
    module: "workforce",
    type: "html_dashboard",
    tier: "primary",
    trust_weight: 0.9,
    metrics: ["placement_rate"],
    use: "baseline_metrics",
  },
  {
    name: "DOL WIOA State Performance",
    url: "https://www.dol.gov/agencies/eta/performance/results/states",
    module: "workforce",
    type: "html_dashboard",
    tier: "primary",
    trust_weight: 0.9,
    metrics: ["placement_rate"],
    use: "baseline_metrics",
  },
  {
    name: "Apprenticeship.gov Data and Statistics",
    url: "https://www.apprenticeship.gov/data-and-statistics",
    module: "workforce",
    type: "html_dashboard",
    tier: "primary",
    trust_weight: 0.9,
    metrics: ["placement_rate"],
    use: "baseline_metrics",
  },
  {
    name: "California EDD QCEW Open Data",
    url: "https://lab.data.ca.gov/dataset/quarterly-census-of-employment-and-wages",
    module: "workforce",
    type: "dataset",
    tier: "primary",
    trust_weight: 0.9,
    metrics: ["employment_rate", "wage_trend"],
    use: "baseline_metrics",
  },
  {
    name: "New York WIOA Program Performance",
    url: "https://dol.ny.gov/wioa-program-performance",
    module: "workforce",
    type: "html_dashboard",
    tier: "primary",
    trust_weight: 0.85,
    metrics: ["placement_rate"],
    use: "baseline_metrics",
  },
  {
    name: "Strada State Opportunity Index",
    url: "https://www.strada.org/state-opportunity-index",
    module: "workforce",
    type: "html_dashboard",
    tier: "secondary",
    trust_weight: 0.75,
    metrics: ["placement_rate", "wage_trend"],
    use: "validation",
  },
];
export const workforce_signal_sources = WORKFORCE_SIGNAL_SOURCES;
export const workforce_data_sources = WORKFORCE_DATA_SOURCES;

export const SEED_SOURCES_BY_MODULE: Record<string, readonly SeedSourceSpec[]> = {
  capital: CAPITAL_SEED,
  education: EDUCATION_SEED,
  policy: POLICY_SEED,
  workforce: WORKFORCE_SIGNAL_SOURCES,
  legal: LEGAL_SEED,
};

export function seedSourcesFor(module: string): readonly SeedSourceSpec[] {
  return SEED_SOURCES_BY_MODULE[module] ?? [];
}

export const DATA_SOURCES_BY_MODULE: Record<string, readonly DataSourceSpec[]> = {
  workforce: WORKFORCE_DATA_SOURCES,
};

export function dataSourcesFor(module: string): readonly DataSourceSpec[] {
  return DATA_SOURCES_BY_MODULE[module] ?? [];
}
